import React, {
	useCallback,
	useEffect,
	useState,
	SyntheticEvent,
	useRef,
	useContext,
} from 'react';

import videojs from 'video.js';
import 'videojs-contrib-hls';
import 'videojs-contrib-quality-levels';
import 'videojs-hls-quality-selector';
import './vtt.plugin';
import { IVideoPlayerProps } from './VideoPlayer.type';
import 'video.js/dist/video-js.css';
import './VideoPlayer.css';
import { debounce, get, isEmpty, isFunction, max, min, omit } from 'lodash';
import styles from './VideoPlayer.module.css';
import classNames from 'classnames';
import {
	calculateAspectRatioFit,
	calculateVideoCropMatrix,
} from './../../utils/video.utils';
import KeyboardEvent = videojs.KeyboardEvent;
import { checkTabletIpadPlatform } from '../../utils/IsPhone';
import { useDispatch, useSelector } from 'react-redux';
import { setTrimCurrentTime, setTrimFirstPlay } from '../../Pages/Flo/Flo.reducers';
import moment from 'moment';
import { analyticsLog } from '../../store/reducers/Log.reducer';
import { MAX_TABLET_VIEW_WIDTH } from '../../Common/Common.params';
import { setAutoPlay } from '../../Pages/Flo/Flo.actions';
import {
	getContrastColor,
	getResourceHeadersForDoc360,
	getThemeDetails,
	getWindowDimensions,
	isMicroApp,
} from '../../Common/Common.utils';
import { ExternalAppInfoContext } from '../../Pages/Flo/Flo';

const playbackRates = [0.5, 1, 1.5, 2];
const initialOptions: videojs.PlayerOptions = {
	controls: false,
	fluid: false,
	playbackRates,
	controlBar: {
		remainingTimeDisplay: true,
		durationDisplay: true,
		currentTimeDisplay: true,
		fullscreenToggle: false,
		pictureInPictureToggle: false,
		volumePanel: {
			inline: false,
		},
	},
	userActions: {
		doubleClick: false,
	},
};

const isHandHeldDevice = checkTabletIpadPlatform();
const timeout = isHandHeldDevice ? 1000 : 500;

export const Video: React.FC<Partial<IVideoPlayerProps>> = ({
	hasInitialhotspot,
	defaultMute,
	onLoadedData,
	autoPlay,
	style,
	addAnnotationHandler,
	addWrapper,
	options,
	ignoreThumbnail,
	containerRef,
	setError,
	setReady,
	volume,
	mute,
	nodeRef,
	className,
	setControls,
	onPlay,
	onUpdate,
	onUpdateRef,
	onEnded,
	onPlayRef,
	onPause,
	onSeeked,
	onSeeking,
	onVolumeChange,
	defaultOptions,
	setPlayerDim,
	setPlayerRef,
	floName,
	createdBy,
	videoSize,
	createdOn,
	offsetWidth,
	offsetHeight,
	onPlaybackRatesChange,
	onChangeTime: onChangeTimeProp,
	canShowCustomControls,
	customControlsChildren,
	appliedCrop,
	hotSpotCropStyles,
	setCroppedVideoStyles,
	hideHeader,
	duration,
	internalArticles,
	withBorder,
	inFullScreen,
	progressRef,
	durationRef,
	isTabView,
	onKeyBoardProgress,
	floId,
	floIndex,
	wrapper,
}) => {
	const manualProgress = useRef(onKeyBoardProgress);
	const dispatch = useDispatch();
	const player = React.useRef<videojs.Player>();
	const defaultProps = defaultOptions || initialOptions;
	const [localReady, setLocalReady] = useState(false);

	const theme = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails['theme']`, 'none')
	);
	const view = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails['view']`, 'default')
	);

	const themeColor = getThemeDetails(theme) || 'none';

	//console.log('Theme color video', themeColor);

	const [containerDim, setContainerDim] = useState({
		width: get(defaultOptions, 'width') || 0,
		height: get(defaultOptions, 'height') || 0,
	});
	// const progressRef = React.useRef<HTMLDivElement>();
	// const durationRef = React.useRef<HTMLDivElement>();
	const trimConfig = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.postProcessConfigs.trim`)
	);
	const interactiveMode = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.floDetails["interactive-mode"]`)
	);
	const skipVideo = useSelector(
		(state) =>
			get(state, `floPage.flos.${floIndex}.floDetails["interactive-mode"]`) &&
			get(state, `floPage.flos.${floIndex}.floDetails["skip-video"]`)
	);
	const isTrimEnable = get(trimConfig, 'enable');
	const timeValue = get(trimConfig, 'timeValue', []);
	const originalLength = get(trimConfig, 'originalLength', 0);
	const rangeValues = get(trimConfig, 'rangeValues');
	const hasNotPlayed = useSelector((state) =>
		get(state, `floPage.flos.${floIndex}.postProcessConfigs.trim.hasNotPlayed`, true)
	);

	const externalAppInfo = useContext(ExternalAppInfoContext);
	const customToken = get(externalAppInfo, 'token', '');

	useEffect(() => {
		manualProgress.current = onKeyBoardProgress;
	}, [onKeyBoardProgress, manualProgress]);

	useEffect(() => {
		if (setReady) {
			setReady(false);
		}
		const source = get(options, 'sources[0].src');
		if (source && player.current) {
			player.current.src(get(options, 'sources[0]', ''));
			if (get(options, 'tracks[0]')) {
				// @ts-ignore
				player.current?.addRemoteTextTrack(get(options, 'tracks[0]'), false);
			}
		}
	}, [setReady, get(options, 'sources[0].src'), player.current]);

	useEffect(() => {
		setContainerDim({
			width: get(defaultOptions, 'width') || 0,
			height: get(defaultOptions, 'height') || 0,
		});
		try {
			if (localReady && player.current) {
				player.current?.width(get(defaultOptions, 'width') || 0);
				player.current?.height(get(defaultOptions, 'height') || 0);
			}
		} catch (e) {
			// console.error('failed to set height and width to player', player);
			dispatch(
				analyticsLog({
					level: 'error',
					message: `failed to set height and width to player ${location.href}`,
					value: get(e, 'stack'),
				})
			);
		}
	}, [defaultOptions, localReady, player, setContainerDim]);
	const resizeHandler = useCallback(() => {
		try {
			if (!setPlayerDim || !containerRef) {
				return;
			}
			const containerWidth = containerRef?.current?.offsetWidth || 0;
			let containerHeight = containerRef?.current?.offsetHeight || 0;

			const windowDimensions = getWindowDimensions(floIndex);

			const { width: oWidth, height: oHeight } = calculateAspectRatioFit(
				get(options, 'sources[0].width') || videoSize?.width || 0,
				get(options, 'sources[0].height') || videoSize?.height || 0,
				(max([16, containerWidth]) as number) -
					(withBorder && !inFullScreen && !isTabView ? 60 : 0),
				(max([9, containerHeight]) as number) -
					(withBorder && !inFullScreen && !isTabView
						? 60
						: !hideHeader
						? windowDimensions.width < MAX_TABLET_VIEW_WIDTH &&
						  windowDimensions.width > 350
							? 24
							: 16
						: 0) -
					(wrapper ? 32 : 0)
			);

			try {
				if (oWidth && localReady && player.current && get(options, 'sources[0].src')) {
					player.current?.height(oHeight);
					player.current?.width(oWidth);
				}
			} catch (e) {
				// console.error('failed to set height and width to player', player);
				dispatch(
					analyticsLog({
						level: 'error',
						message: `failed to set height and width to player ${location.href}`,
						value: get(e, 'stack'),
					})
				);
			}
			if (oWidth)
				setContainerDim({
					height: oHeight,
					width: oWidth,
				});
			const offsetHeight = Math.abs((oHeight - containerHeight) / 2);
			const offsetWidth = Math.abs((oWidth - containerWidth) / 2);
			if (oWidth) {
				setPlayerDim({
					offsetWidth,
					offsetHeight,
					height: oHeight, // - (interactiveMode && isMobileView ? 0 : 9),
					width: oWidth, // - (isMobileView && interactiveMode ? 0 : 16),
				});
			}

			if (oWidth && setCroppedVideoStyles) {
				setCroppedVideoStyles(
					calculateVideoCropMatrix(
						{
							height: oHeight,
							width: oWidth,
						},
						appliedCrop?.videoSize,
						appliedCrop,
						false
					)
				);
			}

			if (setPlayerRef) setPlayerRef(nodeRef?.current);
		} catch (e) {
			// console.error(e);
			dispatch(
				analyticsLog({
					level: 'error',
					message: `resizing ${location.href}`,
					value: get(e, 'stack'),
				})
			);
		}
	}, [
		inFullScreen,
		options,
		internalArticles,
		videoSize,
		setPlayerDim,
		containerRef,
		hideHeader,
		setContainerDim,
		setCroppedVideoStyles,
		offsetWidth,
		localReady,
		offsetHeight,
		appliedCrop,
		setPlayerRef,
		nodeRef,
		player,
		isTabView,
		wrapper,
	]);

	useEffect(() => {
		if (!containerRef?.current) return;
		const throttledHandler = debounce(resizeHandler, timeout);
		const resizeObserver = new ResizeObserver(throttledHandler);
		resizeObserver.observe(containerRef?.current);
		return () => {
			try {
				if (resizeObserver?.disconnect) resizeObserver.disconnect();
			} catch (e) {
				// console.error(e);
				dispatch(
					analyticsLog({
						level: 'error',
						message: `resizeObserver ${location.href}`,
						value: get(e, 'stack'),
					})
				);
			}
		};
	}, [containerRef, resizeHandler]);

	const userHotKeys = useCallback(
		(event: KeyboardEvent) => {
			if (!setPlayerRef || !player.current || skipVideo) return;
			switch (event.key) {
				case String((event.key || '').match(/[0-9]/)): {
					if (interactiveMode) return;
					const ct = player.current?.currentTime() || 0;
					const duration = player.current?.duration();
					const sliceTime = duration / 10;
					player.current?.currentTime(sliceTime * Number(event.key));
					break;
				}
				case 'ArrowLeft': {
					if (interactiveMode) return;
					const ct = player.current?.currentTime() || 0;
					player.current?.currentTime(max([ct - 5, 0]) as number);
					if (isFunction(manualProgress.current)) manualProgress.current(ct, ct - 5);
					break;
				}
				case 'ArrowRight': {
					if (interactiveMode) return;
					const duration = player.current?.duration();
					const ct = player.current?.currentTime() || 0;
					player.current?.currentTime(min([ct + 5, duration]) as number);
					if (isFunction(manualProgress.current)) manualProgress.current(ct, ct + 5);
					break;
				}
				case 'ArrowUp': {
					const volume = player.current?.volume();
					player.current?.volume(min([1, volume + 0.5]) as number);
					break;
				}
				case 'ArrowDown': {
					const volume = player.current?.volume();
					player.current?.volume(min([1, volume - 0.5]) as number);
					break;
				}
				case ' ': {
					dispatch(setAutoPlay({ data: false, floId, floIndex }));
					const isPaused = player.current?.paused();
					if (isPaused) {
						if (onPlayRef?.current) {
							onPlayRef.current();
						}
					} else {
						if (interactiveMode) return;
						player.current?.pause();
					}
					if (onUpdateRef?.current) onUpdateRef.current();
					break;
				}
				case 'm': {
					player.current?.volume(0);
					break;
				}
				case ',': {
					const currRate = player.current?.playbackRate();
					const index = playbackRates.indexOf(currRate);
					const newRate =
						playbackRates[max([0, (index - 1) % playbackRates.length]) as number];
					player.current?.playbackRate(newRate);
					break;
				}
				case '.': {
					const currRate = player.current?.playbackRate();
					const index = playbackRates.indexOf(currRate);
					if ((index + 1) % playbackRates.length === 0) return;
					const newRate =
						playbackRates[
							min([
								(index + 1) % playbackRates.length,
								playbackRates.length - 1,
							]) as number
						];
					player.current?.playbackRate(newRate);
					break;
				}
			}
		},
		[
			onPlayRef,
			skipVideo,
			manualProgress,
			setPlayerRef,
			player,
			onUpdateRef,
			interactiveMode,
			floId,
		]
	);

	const onChangeTime = useCallback(
		(e: SyntheticEvent<HTMLVideoElement, Event>) => {
			if (isFunction(onChangeTimeProp)) {
				onChangeTimeProp(e);
			}
			if (
				!player?.current?.currentTime ||
				!player?.current?.duration ||
				!canShowCustomControls
			)
				return;

			// @ts-ignore
			setVideoCurrentTime(e, originalLength);
			if (progressRef?.current) {
				// @ts-ignore
				let newDuration = player?.current?.duration();
				// @ts-ignore
				let currentTime = player?.current?.currentTime();

				setProgressRef(newDuration, currentTime);
			}
		},
		[
			onChangeTimeProp,
			// @ts-ignore
			timeValue,
			duration,
			canShowCustomControls,
			player,
			rangeValues,
			isTrimEnable,
			originalLength,
		]
	);

	useEffect(() => {
		if (mute && player.current) {
			player.current?.muted(true);
		}
	}, [mute, player.current]);

	useEffect(() => {
		if (!get(options, 'sources[0].src') || !nodeRef?.current) return;
		const enableHlsCredentials = process.env.REACT_APP_ENABLE_HLS_CREDENTIALS === 'true';
		if (!player.current) {
			const overrideNative = !nodeRef.current.canPlayType(
				'application/vnd.apple.mpegurl'
			);
			if (isMicroApp() && customToken) {
				videojs.Vhs.xhr.beforeRequest = (options) => {
					// This will be called for all requests including manifests and segments
					const customHeaders = getResourceHeadersForDoc360(floIndex, customToken);
					options.headers = {
						...options.headers,
						...customHeaders,
					};
					return options;
				};
			}

			// @ts-ignore
			videojs(nodeRef.current, {
				...defaultProps,
				...omit(options, ['tracks']),
				preload: 'metadata',
				html5: {
					vhs: {
						smoothQualityChange: true,
						withCredentials: enableHlsCredentials,
						overrideNative: true,
					},
					hls: {
						overrideNative: overrideNative,
					},
					nativeVideoTracks: !overrideNative,
					nativeAudioTracks: !overrideNative,
					nativeTextTracks: !overrideNative,
				},
				userActions: {
					hotkeys: userHotKeys,
					doubleClick: false,
				},
				ajax: {
					headers:
						isMicroApp() && customToken
							? getResourceHeadersForDoc360(floIndex, customToken)
							: {},
				},
			}).ready(function () {
				player.current = this;
				// @ts-ignore
				if (isFunction(this.hlsQualitySelector)) {
					// @ts-ignore
					this.hlsQualitySelector({
						displayCurrentQuality: true,
					});
				}
				if (
					!ignoreThumbnail &&
					get(options, 'sources[0].hasThumbnails') &&
					// @ts-ignore
					isFunction(this.vttThumbnails)
				) {
					// @ts-ignore
					this.vttThumbnails({
						src: get(options, 'sources[0].thumbnailsPath'),
					});
				}
				if (mute || defaultMute) this.volume(0);
				this.playbackRate(1);
				// if ([Infinity, NaN].includes(this.duration())) {
				// 	// this.currentTime(10);
				// 	const seeking = () => {
				// 		this.off('seeking', seeking);
				// 	};
				// 	this.on('seeking', seeking);
				// 	const seeked = () => {
				// 		this.off('seeked', seeked);
				// 		this.currentTime(0);
				// 		if (setProgressRef) {
				// 			setProgressRef(this.duration(), 0);
				// 		}
				// 	};
				// 	this.currentTime(Number.MAX_SAFE_INTEGER);
				// }
				if (setControls) {
					setControls(this);
				}
				if (autoPlay) {
					dispatch(setTrimFirstPlay({ data: false, floId, floIndex }));
					this.autoplay('muted');
				}
				this.bigPlayButton.on('click', () => {
					if (onUpdateRef?.current) onUpdateRef.current();
				});
				this.on('loadedmetadata', () => {
					if (setProgressRef) {
						setProgressRef(this.duration(), 0);
					}
					if (setReady) {
						setReady(true);
					}
				});
				this.on('error', () => {
					// @ts-ignore
					if (setError) setError(true);
				});
				this.on('ratechange', () => {
					if (onPlaybackRatesChange) {
						onPlaybackRatesChange(this.playbackRate());
					}
				});
				this.on('pause', () => {
					if (onUpdateRef?.current) onUpdateRef.current(false);
					this.bigPlayButton.show();
				});
				this.on('ended', () => {
					if (onEnded) onEnded();
				});
				this.on('volumechange', () => {
					if (this.volume && onVolumeChange && !mute) {
						onVolumeChange({
							isMuted: this.muted(),
							volume: this.volume(),
						});
					}
				});
				setLocalReady(true);
				// @ts-ignore
				if (setPlayerRef) {
					setTimeout(() => {
						resizeHandler();
					}, 1000);
				}
			});
		}
	}, [
		onChangeTime,
		setError,
		resizeHandler,
		get(options, 'sources[0].src'),
		// @ts-ignore
		duration,
		userHotKeys,
		setReady,
		setLocalReady,
		setPlayerDim,
		// @ts-ignore
		nodeRef,
		onUpdateRef,
		onEnded,
		player,
		skipVideo,
		floId,
		floIndex,
		customToken,
	]);

	const onPlayCallback = useCallback(() => {
		if (autoPlay ? hasNotPlayed : true) {
			if (onUpdateRef?.current) onUpdateRef.current(true);
		}
		player.current?.bigPlayButton.hide();
	}, [player.current, onUpdateRef, autoPlay, hasNotPlayed]);

	useEffect(() => {
		if (player.current) {
			player.current.on('play', onPlayCallback);
			return () => {
				// @ts-ignore
				player.current?.off('play', onPlayCallback);
			};
		}
	}, [player, onPlayCallback]);

	useEffect(() => {
		if (player.current && onLoadedData) {
			player.current.on('loadeddata', onLoadedData);

			return () => {
				player.current?.off('loadeddata', onLoadedData);
			};
		}
	}, [player, onLoadedData]);

	useEffect(() => {
		if (player.current) {
			player.current.on('timeupdate', onChangeTime);
			player.current.on('seeked', onSeeked);
			player.current.on('seeking', onSeeking);
			return () => {
				// @ts-ignore
				player.current?.off('timeupdate', onChangeTime);
				// @ts-ignore
				player.current?.off('seeked', onSeeked);
				// @ts-ignore
				player.current?.off('seeking', onSeeking);
			};
		}
	}, [player, onChangeTime, onSeeked, onSeeking]);

	useEffect(() => {
		return () => {
			try {
				if (player.current) player.current?.dispose();
			} catch (e) {
				console.error(e);
			}
		};
	}, [player]);
	const setVideoCurrentTime = useCallback(
		(event: unknown, originalLength: number) => {
			if (!event || !player.current) return;
			// @ts-ignore
			let currentTime = (player.current.currentTime() / player.current.duration()) * 100;
			dispatch(
				setTrimCurrentTime({
					currentTime,
					// @ts-ignore
					currentTimeSeconds: player.current.currentTime(),
					floId,
					floIndex,
				})
			);
		},
		[player, floId]
	);

	const setProgressRef = useCallback(
		(newDuration: number, currentTime: number) => {
			if (!progressRef?.current) return;
			// @ts-ignore
			progressRef.current.style.width = `${
				// @ts-ignore
				(currentTime / newDuration) * 100
			}%`;

			const duration = moment.duration(newDuration || 0, 'seconds');
			let valueTotal = moment
				.utc(duration.as('milliseconds'))
				// @ts-ignore
				.format(((newDuration || 0) >= 60 * 60 ? 'HH:' : '') + 'mm:ss');

			if (!moment.utc(duration.as('milliseconds')).isValid()) {
				valueTotal = '--:--';
			}

			const currentDuration = moment.duration(
				// @ts-ignore
				currentTime || 0,
				'seconds'
			);

			let valueCurrent = moment
				.utc(currentDuration.as('milliseconds'))
				// @ts-ignore
				.format(((currentTime || 0) >= 60 * 60 ? 'HH:' : '') + 'mm:ss');

			const currentValueIsValid = moment
				.utc(currentDuration.as('milliseconds'))
				.isValid();
			if (!currentValueIsValid) {
				valueCurrent = '--:--';
			}
			if (durationRef?.current) {
				// @ts-ignore
				durationRef.current.innerText = `${valueCurrent} / ${valueTotal}`;
			}
		},
		[progressRef, timeValue, durationRef]
	);

	const onClickProgressBar = useCallback(
		(e: React.MouseEvent<HTMLDivElement>) => {
			const target = document.getElementById(`floikCustomProgressBar_${floIndex}`);
			// @ts-ignore
			const { width } = target.getBoundingClientRect();
			const clientX = get(e, 'nativeEvent.offsetX', 0);

			let newDuration = isEmpty(timeValue)
				? player?.current?.duration()
				: timeValue[1] - timeValue[0];
			if (!newDuration) return;
			const ratio = clientX / width;
			const currentTime = ratio * newDuration;
			if (isTrimEnable && isFinite(timeValue[0])) {
				player?.current?.currentTime(currentTime + timeValue[0]);
			} else {
				player?.current?.currentTime(currentTime);
			}
		},
		[isTrimEnable, timeValue, player, floIndex]
	);

	const opacity = !containerDim.width || !containerDim.height ? 0 : 1;
	let video = (
		<video
			// @ts-ignore
			ref={nodeRef}
			style={{
				...(addWrapper
					? {}
					: {
							width: containerDim.width,
							height: containerDim.height,
							opacity,
					  }),
			}}
			playsInline
			webkit-playsinline
			crossOrigin={
				process.env.REACT_APP_ENABLE_HLS_CREDENTIALS === 'true'
					? 'use-credentials'
					: 'anonymous'
			}
			// onSeeked={onSeeked}
			className={classNames('video-js', className, styles.mainVideoPlayer)}
			onContextMenu={(e) => e.preventDefault()}
			// onSeeking={onSeeking}
		/>
	);

	if (addWrapper) {
		return (
			<div
				style={
					{
						...(style || {}),
						width:
							inFullScreen || (canShowCustomControls && hideHeader)
								? '100%'
								: containerDim.width,
						height:
							inFullScreen || (canShowCustomControls && hideHeader)
								? 'fit-content'
								: containerDim.height,
						opacity,
						'--container-background':
							themeColor === 'none' ? 'var(--global-themeColor)' : themeColor,
						'--container-opacity': themeColor === 'none' ? '0.03' : '1',
					} as React.CSSProperties
				}
				data-width={`${containerDim.width}px`}
				data-height={`${containerDim.height}px`}
				className={classNames(styles.videoOverallWrapper, {
					[styles.canShowCustomControls]: canShowCustomControls,
					[styles.withBorder]: withBorder && !inFullScreen,
					[styles.withWrapper]: wrapper,
					[styles.videoFullScreen]: inFullScreen || hideHeader,
					[styles.videoInteractive]: interactiveMode,
					[styles.videoFullScreenInteractive]:
						interactiveMode && (inFullScreen || hideHeader),
				})}
			>
				{video}
				{className === 'screenShareTrack' && (
					<style id="screenShareTrackStyles">{`.screenShareTrack video {
					transform: ${get(hotSpotCropStyles, 'transform', '')} !important;
					transform-origin: ${get(hotSpotCropStyles, 'transformOrigin', '0 0')} !important;
					transition: transform linear ${500}ms;
					left: ${(get(hotSpotCropStyles, 'translateX', 0) || 0) * -1}px !important;
					top: ${(get(hotSpotCropStyles, 'translateY', 0) || 0) * -1}px !important;
				}`}</style>
				)}
			</div>
		);
	}
	return video;
};

export default Video;
